/**
 * Is the agentic "Otto Agent" available to the signed-in staff user, and which
 * skills? A skill is offered only when the user holds ALL of its permissions
 * (invoice/quote need `crm:financials`; a campaign needs `crm:view`). If none
 * qualify, the whole surface is hidden. This is the staff-side gate; the gateway
 * enforces the same server-side.
 */
import { useMemo } from 'react';

import {
  OTTO_AGENT_SKILLS,
  OTTO_SKILL_PERMISSIONS,
  hasPermission,
  type OttoAgentSkillId,
} from '@xop/dashboard-shared';

import { useAuthOptional } from '../auth/AuthProvider.js';

export interface AgenticCapability {
  /** True when at least one skill is available. Gates the launcher. */
  enabled: boolean;
  /** The skills this user may run (already permission-filtered). */
  skills: OttoAgentSkillId[];
  /** Non-authoritative actor hints passed to the gateway for prompt framing. */
  actor: {
    role?: string;
    name?: string;
    can_financials: boolean;
    can_view_all: boolean;
  };
}

export function useAgenticEnabled(): AgenticCapability {
  // Non-throwing: a drop-in "Create with AI" button may render in a page unit
  // test with no <AuthProvider> — degrade to "disabled" instead of crashing.
  const user = useAuthOptional()?.user ?? null;
  return useMemo(() => {
    const perms = user?.permissions;
    const skills = OTTO_AGENT_SKILLS.filter((s) =>
      OTTO_SKILL_PERMISSIONS[s].every((p) => hasPermission(perms, p)),
    );
    return {
      enabled: skills.length > 0,
      skills,
      actor: {
        ...(user?.dashboard_role ? { role: user.dashboard_role } : {}),
        ...(user?.name ? { name: user.name } : {}),
        can_financials: hasPermission(perms, 'crm:financials'),
        can_view_all: hasPermission(perms, 'crm:view_all'),
      },
    };
  }, [user]);
}
